import { create } from 'zustand'
import {
  botConfigErrorCode,
  getBotConfig,
  listPlaybookPlaceholders,
  listPlaybookTemplates,
  publishBotConfig,
  saveBotDraft,
} from '../api/botConfig'

const EMPTY_CONTENT = {}

function clone(value) {
  if (value === null || value === undefined) return value
  return JSON.parse(JSON.stringify(value))
}

function normalizePlaybooks(playbooks) {
  return Array.isArray(playbooks) ? clone(playbooks) : []
}

function normalizeConfig(content, playbooks = []) {
  return { ...(content && typeof content === 'object' ? clone(content) : {}), playbooks: normalizePlaybooks(playbooks) }
}

function normalizeAction(action) {
  const source = action && typeof action === 'object' ? action : {}
  return { kind: source.kind ?? '', params: clone(source.params ?? {}) }
}

function starterConfig(template) {
  const source = template.default_config ?? template
  return {
    id: source.id ?? template.id,
    type: source.type ?? template.type,
    label: source.label ?? template.label,
    enabled: true,
    trigger: clone(source.trigger ?? { description: '', keywords: [] }),
    action: normalizeAction(source.action),
    confirmation: clone(source.confirmation ?? { confirm_first: true, closing_text: '' }),
    policies_text: source.policies_text ?? '',
  }
}

function normalizeTemplates(payload) {
  const templates = Array.isArray(payload?.templates) ? payload.templates : []
  return templates.map((template) => ({ ...template, default_config: starterConfig(template) }))
}

function capabilitiesFromSnapshot(snapshot) {
  const source = snapshot.capabilities ?? {}
  return {
    ...source,
    can_read: source.can_read ?? true,
    can_write: source.can_write ?? true,
    can_publish: source.can_publish ?? source.can_write ?? true,
  }
}

const initialState = {
  tenantId: null, published: null, draft: normalizeConfig(EMPTY_CONTENT),
  draftVersion: 0, publishedVersion: 0, capabilities: null, templates: [],
  playbookSlots: {}, placeholders: [], actionOptions: [],
  limits: { max_active_playbooks: 3 }, hasUnpublishedChanges: false,
  dirty: false, loading: false, saving: false, error: null, conflict: null,
  sandboxState: null, sandboxSlots: {},
}

const useBotConfigStore = create((set, get) => ({
  ...initialState,

  reset: () => set({ ...initialState, draft: normalizeConfig(EMPTY_CONTENT), _activeRequest: Symbol('bot-config-reset') }),

  setSnapshot: (tenantId, snapshot = {}, catalogue = {}) => {
    const draftContent = snapshot.draft ?? snapshot.published ?? EMPTY_CONTENT
    const playbooks = snapshot.draft
      ? snapshot.draft_playbooks
      : (snapshot.published_playbooks ?? snapshot.draft_playbooks)
    const templates = catalogue.templates ?? normalizeTemplates(snapshot.templates)
    const actionOptions = catalogue.actionOptions ?? snapshot.action_options ?? templates.map((template) => ({
      type: template.type,
      action: template.action?.kind,
      available: template.available !== false && (catalogue.availableActions ?? []).includes(template.action?.kind),
    }))
    set({
      ...initialState, tenantId,
      published: snapshot.published ? { ...clone(snapshot.published), playbooks: normalizePlaybooks(snapshot.published_playbooks) } : null,
      draft: normalizeConfig(draftContent, playbooks),
      draftVersion: snapshot.draft_version ?? 0,
      publishedVersion: snapshot.version ?? 0,
      capabilities: capabilitiesFromSnapshot(snapshot),
      templates, playbookSlots: snapshot.playbook_slots ?? {},
      placeholders: catalogue.placeholders ?? snapshot.placeholders ?? [],
      actionOptions, limits: { ...initialState.limits, ...(snapshot.limits ?? {}) },
      hasUnpublishedChanges: snapshot.has_unpublished_changes ?? Boolean(snapshot.draft),
      sandboxState: null, sandboxSlots: {},
    })
  },

  load: async (tenantId) => {
    if (!tenantId) return null
    const requestId = Symbol('bot-config-load')
    set({ ...initialState, tenantId, loading: true, draft: normalizeConfig(EMPTY_CONTENT), _activeRequest: requestId })
    try {
      const [snapshot, templatePayload] = await Promise.all([getBotConfig(tenantId), listPlaybookTemplates()])
      const templates = normalizeTemplates(templatePayload)
      const types = new Set(templates.map((template) => template.type))
      // Placeholder chips are optional; one failing type must not hide the editor.
      const placeholderPayloads = await Promise.all([...types].map((type) => listPlaybookPlaceholders(type).catch(() => null)))
      const placeholders = placeholderPayloads.filter(Boolean).flatMap((payload) => (payload?.placeholders ?? []).map((placeholder) => ({ ...placeholder, type: payload.type })))
      const availableActions = templatePayload?.available_actions ?? []
      const actionOptions = templates.map((template) => ({
        type: template.type, action: template.action?.kind,
        available: template.available !== false && availableActions.includes(template.action?.kind),
      }))
      if (get()._activeRequest !== requestId) return snapshot
      get().setSnapshot(tenantId, snapshot, { templates, placeholders, actionOptions, availableActions })
      return snapshot
    } catch (error) {
      if (get()._activeRequest === requestId) set({ loading: false, error })
      throw error
    }
  },

  togglePlaybook: (starter) => {
    const state = get()
    if (!state.capabilities?.can_write || starter?.available === false) return false
    const playbooks = [...state.draft.playbooks]
    const index = playbooks.findIndex((playbook) => playbook.type === starter.type)
    if (index >= 0) {
      if (playbooks[index].enabled === false) {
        const max = state.limits.max_active_playbooks ?? 3
        if (playbooks.filter((playbook) => playbook.enabled !== false).length >= max) return false
        playbooks[index] = { ...playbooks[index], enabled: true }
      } else {
        playbooks[index] = { ...playbooks[index], enabled: false }
      }
    } else {
      const max = state.limits.max_active_playbooks ?? 3
      if (playbooks.filter((playbook) => playbook.enabled !== false).length >= max) return false
      playbooks.push(starterConfig(starter))
    }
    set({ draft: { ...state.draft, playbooks }, dirty: true, hasUnpublishedChanges: true, conflict: null })
    return true
  },

  updateDraft: (changes) => set((state) => ({
    draft: normalizeConfig({ ...state.draft, ...changes }, state.draft.playbooks),
    dirty: true, hasUnpublishedChanges: true, conflict: null,
  })),

  updatePlaybook: (type, changes) => set((state) => ({
    draft: normalizeConfig(state.draft, state.draft.playbooks.map((playbook) => playbook.type === type ? { ...playbook, ...changes } : playbook)),
    dirty: true, hasUnpublishedChanges: true, conflict: null,
  })),

  saveDraft: async () => {
    const { tenantId, draft, draftVersion } = get()
    if (!tenantId) return null
    set({ saving: true, error: null })
    try {
      const { playbooks, ...content } = clone(draft)
      const response = await saveBotDraft(tenantId, content, draftVersion, playbooks)
      set((state) => ({
        draft: normalizeConfig(response?.content ?? content, playbooks),
        draftVersion: response?.draft_version ?? state.draftVersion + 1,
        dirty: false, saving: false, hasUnpublishedChanges: true, conflict: null,
      }))
      return response
    } catch (error) {
      set({ saving: false, error, conflict: error?.response?.status === 409 ? error : null })
      throw error
    }
  },

  publishDraft: async (confirmGatesChange = false) => {
    const state = get()
    if (!state.tenantId) return null
    if (state.dirty) await get().saveDraft()
    const { tenantId, draftVersion, publishedVersion } = get()
    set({ saving: true, error: null })
    try {
      const response = await publishBotConfig(tenantId, publishedVersion, draftVersion, confirmGatesChange)
      const published = response?.published ?? {}
      set((current) => ({
        published: { ...published, playbooks: normalizePlaybooks(published.playbooks ?? current.draft.playbooks) },
        publishedVersion: response?.version ?? current.publishedVersion + 1,
        hasUnpublishedChanges: Boolean(response?.has_unpublished_changes), saving: false,
      }))
      return response
    } catch (error) {
      // gates_change_unconfirmed is handled by the confirmation dialog, not the error banner.
      set({ saving: false, error: botConfigErrorCode(error) === 'gates_change_unconfirmed' ? null : error })
      throw error
    }
  },

  setSandboxState: (state, slots = {}) => set({ sandboxState: state, sandboxSlots: slots }),
}))

export default useBotConfigStore


